import { Alert, Button, Card, Col, Form, Row } from 'antd';
import React, { useEffect, useMemo } from 'react';

import ConfirmModal from 'components/theme_components/ThirdParty/ConfirmModal';
import type { FormInstance } from 'antd/lib';
import GetLabel from 'DisplayFramework/components/GetLabel';
import IPen from 'icons/IPen';
import ModalSFS from 'components/theme_components/ModalSFS';
import { PlusOutlined } from '@ant-design/icons';
import ProductParticipationModal from './ProductModals/ProductParticipationModal';
import ProductTauxModal from './ProductModals/ProductRateModal';
import Title from 'components/Title';

export type ProductParticipationProps = {
  form: FormInstance;
  isInspect: any;
  modalVisible: boolean;
  prefinlist: any[];
  setVisible: (visible: boolean) => void;
  updateRate: (item: any) => void;
  addRate: (item: any) => void;
  setModalVisible: (modalVisible: boolean) => void;
  franchiseperiod?: number;
  onValidationChange?: (isValid: boolean) => void; // Add validation callback
};

const ProductParticipation = ({
  form,
  isInspect,
  prefinlist,
  modalVisible,
  updateRate,
  addRate,
  setVisible,
  setModalVisible,
  franchiseperiod = 0,
  onValidationChange,
}: ProductParticipationProps) => {
  // Calculate the sum of all ratenbdaysmax values from the list
  const totalRateDays = useMemo(() => {
    return prefinlist.reduce((sum, item) => {
      const nscDays = item?.nsc?.ratenbdaysmax || 0;
      return sum + Number(nscDays);
    }, 0);
  }, [prefinlist]);

  const durationmax = Number(form.getFieldValue('durationmax') || 0);

  // Check if total matches durationmax - this is the key validation
  const isValidTotal = useMemo(() => {
    return totalRateDays === franchiseperiod;
  }, [totalRateDays, franchiseperiod]);


  // Notify parent about validation status to disable next button
  useEffect(() => {
    onValidationChange?.(isValidTotal);
  }, [isValidTotal, onValidationChange]);

  // Check if button should be displayed
  const shouldShowAddButton =
    prefinlist.length === 0 ||
    (totalRateDays < durationmax && totalRateDays < franchiseperiod);

  // Group rates by period for display
  const groupedRates = useMemo(() => {
    const ratesByPeriod: {
      [key: string]: { nsc?: any; client?: any };
    } = {};

    prefinlist.forEach((item) => {
      const periodNumber = item.nsc?.ctesorder || item.client?.ctesorder;
      if (!ratesByPeriod[periodNumber]) {
        ratesByPeriod[periodNumber] = {
          nsc: item.nsc || {},
          client: item.client || {},
        };
      } else {
        // If we already have an entry for this period, update it
        if (item.nsc) ratesByPeriod[periodNumber].nsc = item.nsc;
        if (item.client) ratesByPeriod[periodNumber].client = item.client;
      }
    });

    // Sort by period number
    return Object.values(ratesByPeriod).sort(
      (a, b) =>
        (a.nsc?.ctesorder || a.client?.ctesorder) -
        (b.nsc?.ctesorder || b.client?.ctesorder)
    );
  }, [prefinlist]);

  return (
    <Form className="container-stepper mt-4 taux productlist" form={form}>
      <Title
        comcode="infoToCompletetaux"
        level={4}
        className="ut-updateTP-title mb-7 mt-10"
      />

      <Row
        className="mb-4 px-6"
        gutter={24}
        justify="space-between"
        align="middle"
      >
        <Col span={2}>
          <GetLabel
            label="periode"
            className=" font-size-13 font-weight-600 gray-color"
          />
        </Col>
        <Col span={2}>
          <GetLabel
            label="DebutJ"
            className=" font-size-13 font-weight-600 gray-color"
          />
        </Col>
        <Col span={2}>
          <GetLabel
            label="nbJours"
            className=" font-size-13 font-weight-600 gray-color"
          />
        </Col>
        <Col span={2}>
          <GetLabel
            label="FinJ"
            className=" font-size-13 font-weight-600 gray-color"
          />
        </Col>
        <Col span={6}>
          <GetLabel
            label="ConditNSC"
            className=" font-size-13 font-weight-600 gray-color"
          />
        </Col>
        <Col span={2}></Col>
      </Row>

      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <Form.Item shouldUpdate noStyle>
          {groupedRates?.length === 0 && (
            <Card
              style={{ zIndex: 100 }}
              className="no-border-radius no-border text-center mb-4"
            >
              <GetLabel label="tauxVid" className="font-size-13 gray-color " />
            </Card>
          )}
          {groupedRates?.map((item: any) => (
            <Card
              key={item?.nsc?.key || item?.client?.key}
              className="activity-card mb-4"
            >
              <Row gutter={16} align="middle" justify="space-between">
                <Col span={2}>
                  <div className="circle-number">
                    {item?.nsc?.ctesorder || item?.client?.ctesorder || '-'}
                  </div>
                </Col>
                <Col span={2}>
                  {item?.nsc?.ratestartday || item?.client?.ratestartday || '-'}
                </Col>
                <Col span={2}>
                  {item?.nsc?.ratenbdaysmax ||
                    item?.client?.ratenbdaysmax ||
                    '-'}
                </Col>
                <Col span={2}>
                  {item?.nsc?.rateendday || item?.client?.rateendday || '-'}
                </Col>
                <Col span={6}>
                  <div>
                    <span className="gray-color">
                      <GetLabel label="rateformula" />
                    </span>{' '}
                    {item?.nsc?.nscrateformulalabel || item?.nsc?.nscrateformula || 'N/A'}                  </div>
                  <div>
                    <span className="gray-color">
                      <GetLabel label="appliedrate" />
                    </span>{' '}
                    {item?.nsc?.nscappliedrate != null
                      ? item?.nsc?.nscappliedrate.toString().replace('.', ',') +
                      ' %'
                      : 'N/A'}
                  </div>
                </Col>
                <Col span={2}>
                  <ModalSFS
                    className="color-gray"
                    width={560}
                    trigger={({ setVisible }) => (
                      <IPen className="IPen" onClick={() => setVisible(true)} />
                    )}
                    title="updateTaux"
                  >
                    <ProductParticipationModal
                      isCreate={false}
                      durationmax={form.getFieldValue('durationmax')}
                      franchiseperiod={franchiseperiod}
                      prefsteplist={prefinlist}
                      setVisible={setVisible}
                      data={item}
                      onSaveRate={(newItems) => {
                        updateRate(newItems);
                      }}
                    />
                  </ModalSFS>
                </Col>
              </Row>
            </Card>
          ))}
        </Form.Item>
      </div>

      {/* Button and Modal Section */}
      <Row justify="center" align="middle">
        {shouldShowAddButton && (
          <ModalSFS
            className="color-gray"
            width={560}
            trigger={({ setVisible }) => (
              <Button
                className="ut-reset-btn ut-btn-no-border no-box-shadow font-weight-500"
                onClick={() => setVisible(true)}
                icon={<PlusOutlined />}
              >
                <GetLabel label="addPeriode" />
              </Button>
            )}
            title="createTaux"
          >
            <ProductParticipationModal
              isCreate
              durationmax={form.getFieldValue('durationmax')}
              franchiseperiod={franchiseperiod}
              prefsteplist={prefinlist ?? []}
              setVisible={setVisible}
              onSaveRate={addRate}
            />
          </ModalSFS>
        )}
        <ConfirmModal
          visible={modalVisible}
          status="error"
          message="Êtes-vous sûr de vouloir supprimer la période  ? "
          onConfirm={() => {
            setModalVisible(false);
          }}
          onCancel={() => !isInspect && setModalVisible(false)}
          className="modalConfirmOrCancel"
        />
      </Row>
    </Form>
  );
};

export default ProductParticipation;
